import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useSnackbar } from 'notistack';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import Hidden from '@material-ui/core/Hidden';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ParcelsDialog from '../ParcelsDialog';
import Icon from '@afosto/app-components-react/Icon';
import { parcels as parcelsApi } from '@afosto/fulfilment/api/Parcels';
import translations from './translations';

const Parcel = ({
  allowCreate,
  currentParcel,
  onBeforeCreate,
  onBeforeSelect,
  onCreate,
  onSelect,
  parcels,
  parcelQuantities,
  shipmentId,
  isOpen,
  onClose,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [showParcelsDialog, setShowParcelsDialog] = useState(false);
  const [isCreatingParcel, setIsCreatingParcel] = useState(false);
  const [isSelectingParcel, setIsSelectingParcel] = useState(false);
  const canCreate = allowCreate && shipmentId;
  const quantity = parcelQuantities[currentParcel?.id] ?? currentParcel?.items?.length;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  useEffect(() => {
    if (isOpen !== undefined) {
      setShowParcelsDialog(!!isOpen);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    }

    setShowParcelsDialog(false);
  };

  const handleCreateParcel = async () => {
    setIsCreatingParcel(true);

    try {
      if (onBeforeCreate && typeof onBeforeCreate === 'function') {
        await onBeforeCreate();
      }

      const response = await parcelsApi.createParcel({
        shipment_id: shipmentId,
      });
      const { data: newParcel } = response;

      if (onCreate && typeof onCreate === 'function') {
        await onCreate(newParcel);
      }

      handleClose();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotCreated), {
        variant: 'error',
      });
    } finally {
      setIsCreatingParcel(false);
    }
  };

  const handleSelectParcel = async parcel => {
    setIsSelectingParcel(true);

    try {
      if (onBeforeSelect && typeof onBeforeSelect === 'function') {
        await onBeforeSelect(parcel);
      }

      if (onSelect && typeof onSelect === 'function') {
        await onSelect(parcel);
      }

      handleClose();
      setIsSelectingParcel(false);
    } catch (err) {
      handleClose();

      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotSelected), {
        variant: 'error',
      });
    } finally {
      setIsSelectingParcel(false);
    }
  };

  const handleListItemClick = () => {
    if (isMobile) {
      if (parcels.length > 1) {
        setShowParcelsDialog(true);
      } else {
        handleCreateParcel();
      }
    }
  };

  return (
    <>
      <Card>
        <List disablePadding>
          <ListItem button={isMobile} onClick={handleListItemClick}>
            <ListItemIcon>
              <Icon icon={['fal', 'box']} />
            </ListItemIcon>
            {currentParcel ? (
              <>
                <ListItemText
                  primary={currentParcel?.number}
                  secondary={intl.formatMessage(translations.numberOfItems, {
                    number: quantity ?? 0,
                  })}
                />
                <Hidden implementation="css" mdUp>
                  <Icon icon={['fal', parcels.length > 1 ? 'exchange' : 'plus']} color="primary" />
                </Hidden>
                <Hidden implementation="css" smDown>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      if (parcels.length > 1) {
                        setShowParcelsDialog(true);
                        return;
                      }

                      handleCreateParcel();
                    }}
                  >
                    {parcels.length > 1
                      ? intl.formatMessage(translations.actions.edit)
                      : intl.formatMessage(translations.actions.new)}
                  </Button>
                </Hidden>
              </>
            ) : (
              <>
                <ListItemText
                  primary={intl.formatMessage(translations.noParcelMessage)}
                  primaryTypographyProps={{ color: 'textSecondary' }}
                  secondary={intl.formatMessage(translations.createNewParcelMessage)}
                />
                {canCreate && (
                  <>
                    <Hidden implementation="css" mdUp>
                      <Icon icon={['fal', 'plus']} color="primary" />
                    </Hidden>
                    <Hidden implementation="css" smDown>
                      <Button variant="outlined" color="primary" onClick={handleCreateParcel}>
                        {intl.formatMessage(translations.actions.new)}
                      </Button>
                    </Hidden>
                  </>
                )}
              </>
            )}
          </ListItem>
        </List>
      </Card>
      <ParcelsDialog
        allowCreate={allowCreate}
        isOpen={showParcelsDialog}
        isLoading={isCreatingParcel || isSelectingParcel}
        parcels={parcels}
        selectedParcel={currentParcel}
        parcelQuantities={parcelQuantities}
        onClose={handleClose}
        onSelect={handleSelectParcel}
        onCreate={handleCreateParcel}
      />
    </>
  );
};

Parcel.propTypes = {
  allowCreate: PropTypes.bool,
  currentParcel: PropTypes.object,
  isOpen: PropTypes.bool,
  onBeforeCreate: PropTypes.func,
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  onBeforeSelect: PropTypes.func,
  onSelect: PropTypes.func,
  parcels: PropTypes.array,
  parcelQuantities: PropTypes.object,
  shipmentId: PropTypes.string,
};

Parcel.defaultProps = {
  allowCreate: true,
  currentParcel: null,
  isOpen: undefined,
  onBeforeCreate: undefined,
  onClose: undefined,
  onCreate: undefined,
  onBeforeSelect: undefined,
  onSelect: undefined,
  parcels: [],
  parcelQuantities: {},
  shipmentId: undefined,
};

export default Parcel;
